import { Theme, alpha } from '@mui/material/styles';

export const getNavigationStyles = (theme: Theme) => ({
  root: {
    width: '100%',
    position: 'fixed',
    top: 0,
    left: 0,
    zIndex: theme.zIndex.appBar,
    height: { xs: 64, sm: 72 },
  },
  navbar: {
    container: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: theme.spacing(2, 3),
      minHeight: { xs: 64, sm: 72 },
      [theme.breakpoints.down('sm')]: {
        padding: theme.spacing(1.5, 2),
      },
    },
    default: {
      background: alpha(theme.palette.background.paper, 0.98),
      boxShadow: '0 4px 30px rgba(0, 0, 0, 0.1)',
      backdropFilter: 'blur(10px)',
      borderBottom: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
    },
    transparent: {
      background: 'transparent',
      boxShadow: 'none',
      backdropFilter: 'none',
    },
    scrolled: {
      background: alpha(theme.palette.background.paper, 0.98),
      boxShadow: '0 4px 30px rgba(0, 0, 0, 0.1)',
      backdropFilter: 'blur(10px)',
      borderBottom: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
    },
    transition: {
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    },
  },
  logo: {
    container: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1),
      transition: 'opacity 0.2s ease-out',
      '&:hover': {
        opacity: 0.9,
      },
    },
    image: {
      height: 32,
      width: 'auto',
      transition: 'filter 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      [theme.breakpoints.down('sm')]: {
        height: 28,
      },
    },
    text: {
      fontSize: '1.25rem',
      fontWeight: 600,
      color: theme.palette.text.primary,
      textDecoration: 'none',
      transition: 'color 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      [theme.breakpoints.down('sm')]: {
        fontSize: '1.1rem',
      },
    },
  },
  menu: {
    desktop: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(0.5),
      marginLeft: 'auto',
      [theme.breakpoints.down('md')]: {
        display: 'none',
      },
    },
    mobile: {
      display: 'none',
      [theme.breakpoints.down('md')]: {
        display: 'flex',
      },
    },
  },
  dropdown: {
    container: {
      position: 'relative',
      '&::after': {
        content: '""',
        position: 'absolute',
        bottom: -8,
        left: 0,
        right: 0,
        height: 8,
      },
    },
    paper: {
      background: alpha(theme.palette.background.paper, 0.98),
      backdropFilter: 'blur(10px)',
      border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
      borderRadius: '12px',
      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
      overflow: 'hidden',
      transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
      minWidth: 220,
      '& .MuiMenuItem-root': {
        color: theme.palette.text.primary,
        '&:hover': {
          color: theme.palette.primary.main,
          background: alpha(theme.palette.action.hover, 0.08),
        },
      },
    },
    item: {
      color: theme.palette.text.primary,
      padding: '12px 20px',
      transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1),
      fontSize: '0.95rem',
      fontWeight: 500,
      '&:hover': {
        color: theme.palette.primary.main,
        background: alpha(theme.palette.action.hover, 0.1),
        transform: 'translateX(4px)',
      },
      '& .MuiSvgIcon-root': {
        fontSize: '1.1rem',
        opacity: 0.7,
        color: 'inherit',
      },
    },
    icon: {
      marginLeft: 'auto',
      opacity: 0.7,
      fontSize: '1.1rem',
      transition: 'transform 0.2s ease-out',
    },
  },
  button: {
    nav: {
      color: theme.palette.text.primary,
      fontSize: '0.95rem',
      fontWeight: 500,
      padding: '8px 16px',
      transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
      opacity: 0.9,
      minWidth: 'auto',
      borderRadius: '8px',
      '&:hover': {
        opacity: 1,
        color: theme.palette.primary.main,
        background: alpha(theme.palette.action.hover, 0.1),
      },
      '&.active, &[data-active="true"]': {
        opacity: 1,
        background: alpha(theme.palette.action.selected, 0.15),
        color: theme.palette.primary.main,
      },
    },
    auth: {
      login: {
        color: theme.palette.text.primary,
        fontSize: '0.95rem',
        fontWeight: 500,
        padding: '8px 16px',
        transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
        opacity: 0.9,
        minWidth: 'auto',
        borderRadius: '8px',
        '&:hover': {
          opacity: 1,
          color: theme.palette.primary.main,
          background: alpha(theme.palette.action.hover, 0.1),
        },
      },
      register: {
        background: theme.palette.primary.main,
        color: theme.palette.common.white,
        transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
        fontWeight: 600,
        padding: '8px 16px',
        minWidth: 'auto',
        marginLeft: theme.spacing(0.5),
        borderRadius: '8px',
        '&:hover': {
          background: theme.palette.primary.dark,
          transform: 'translateY(-1px)',
          boxShadow: theme.shadows[4],
        },
      },
    },
  },
  mobile: {
    drawer: {
      width: 300,
      '& .MuiDrawer-paper': {
        width: 300,
        background: theme.palette.background.paper,
        color: theme.palette.text.primary,
        borderLeft: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
      },
    },
    header: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: theme.spacing(2),
      borderBottom: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
      background: alpha(theme.palette.action.hover, 0.03),
    },
    list: {
      padding: theme.spacing(2, 0),
    },
    item: {
      margin: theme.spacing(0.5, 2),
      borderRadius: '8px',
      color: theme.palette.text.primary,
      padding: '12px 16px',
      transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
      '&:hover': {
        color: theme.palette.primary.main,
        background: alpha(theme.palette.action.hover, 0.1),
        transform: 'translateX(4px)',
      },
      '& .MuiListItemText-primary': {
        fontSize: '0.95rem',
        fontWeight: 500,
      },
      '& .MuiIconButton-root': {
        marginRight: -8,
        color: alpha(theme.palette.text.primary, 0.5),
        transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
        '&:hover': {
          color: theme.palette.primary.main,
          background: 'transparent',
        },
      },
    },
    activeItem: {
      background: alpha(theme.palette.action.selected, 0.15),
      color: theme.palette.primary.main,
    },
    subItem: {
      margin: theme.spacing(0.5, 2, 0.5, 4),
      borderRadius: '8px',
      padding: '12px 16px',
      opacity: 0.85,
      transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
      '&:hover': {
        opacity: 1,
        color: theme.palette.primary.main,
        background: alpha(theme.palette.action.hover, 0.1),
        transform: 'translateX(4px)',
      },
      '& .MuiListItemText-primary': {
        fontSize: '0.9rem',
      },
    },
    collapse: {
      '& .MuiCollapse-wrapper': {
        borderRadius: '8px',
        background: alpha(theme.palette.action.hover, 0.03),
        margin: theme.spacing(0.5, 2),
      },
    },
    divider: {
      margin: theme.spacing(2, 2),
      borderColor: alpha(theme.palette.divider, 0.08),
    },
  },
  animations: {
    fadeIn: {
      '@keyframes fadeIn': {
        from: {
          opacity: 0,
          transform: 'translateY(-8px)',
        },
        to: {
          opacity: 1,
          transform: 'translateY(0)',
        },
      },
      animation: 'fadeIn 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
    },
  },
});

// Utility types for type safety
export type NavigationStyles = ReturnType<typeof getNavigationStyles>;
export type NavigationStyleKey = keyof NavigationStyles; 